<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { computed, ref, useId, useTemplateRef } from 'vue';
import DButton from '#layers/ui/components/Button.vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';

defineOptions({
	name: 'ExpandableContent',
});

const props = withDefaults(defineProps<Props>(), {
	fadeColor: 'var(--ui-bg)',
});

interface Props {
	maxHeight: number;
	fadeColor?: string;
	/** Extra classes merged onto the expand/collapse toggle button. */
	toggleClass?: string;
}

const isExpanded = ref(false);
const contentRef = useTemplateRef<HTMLElement>('content');
const contentHeight = ref(0);
const contentId = useId();

useResizeObserver(contentRef, ([entry]) => {
	if (entry) contentHeight.value = entry.contentRect.height;
});

const overflows = computed(() => contentHeight.value > props.maxHeight);

const maxHeightStyle = computed(() => {
	if (contentHeight.value === 0) return;
	const target = isExpanded.value || !overflows.value
		? contentHeight.value
		: props.maxHeight;
	return `${target}px`;
});

const fadeStyle = computed(() => ({
	background: `linear-gradient(to top, ${props.fadeColor}, transparent)`,
}));

function toggle() {
	isExpanded.value = !isExpanded.value;
}

function collapse() {
	isExpanded.value = false;
}
</script>

<template>
	<div class="relative min-w-0 flex-1">
		<div
			:id="contentId"
			class="overflow-hidden transition-[max-height] duration-200 ease-out"
			:style="{ maxHeight: maxHeightStyle }"
		>
			<div ref="content">
				<slot :expanded="isExpanded" :overflows :collapse />
			</div>
		</div>

		<div
			v-if="overflows && !isExpanded"
			class="pointer-events-none absolute bottom-0 h-40 w-full"
			:style="fadeStyle"
		/>

		<DTooltip v-if="overflows" :text="isExpanded ? $t('common.collapse') : $t('common.expand')">
			<DButton
				:label="isExpanded ? $t('common.collapse') : $t('common.expand')"
				:aria-expanded="isExpanded"
				:aria-controls="contentId"
				variant="outline"
				size="sm"
				color="neutral"
				class="absolute inset-s-1/2 bottom-4 -translate-x-1/2 shadow-sm"
				:class="toggleClass"
				rounded
				@click="toggle"
			>
				<template #trailing>
					<DIcon
						name="lucide:chevron-down"
						class="size-3.5 transition-transform duration-200"
						:class="{ 'rotate-180': isExpanded }"
					/>
				</template>
			</DButton>
		</DTooltip>
	</div>
</template>
